import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_KINDS,
  emailDelivery,
  type NotificationChannel,
  type NotificationKind,
  type NotificationPreference,
  type WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { PushDeviceCard } from "@/components/notifications/PushDeviceCard";
import { NOTIFICATIONS_KEY } from "@/components/notifications/queries";
import { LoadFailed } from "@/components/shell/EmptyState";
import { PageContainer } from "@/components/shell/PageContainer";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

const PREFERENCES_KEY = [...NOTIFICATIONS_KEY, "preferences"] as const;

function describe(rejection: WriteRejection): string {
  switch (rejection.kind) {
    case "blocked":
    case "channel_rejected":
      return rejection.message;
    case "conflict":
      return rejection.message;
    case "forbidden":
      return `${rejection.message} (requires ${rejection.permission})`;
    case "invalid":
      return rejection.fields
        ? Object.values(rejection.fields).join("; ")
        : rejection.message;
  }
}

/**
 * Notifications, in the signed-in person's own account (E6, #446, slice 1):
 * for each kind of event, whether it also reaches them by email and by phone
 * push.
 *
 * The bell is always on — it is shown ticked and cannot be changed. Email sends
 * (slice 4): assignments and mentions at once, everything else in the morning
 * digest, and each kind says which. Push is switched on per device in the card
 * below.
 *
 * Not a Settings-module section: those are administration, each behind a
 * permission, and this is one every signed-in person owns. It has no
 * permission to hold — the API scopes it to the session's own user.
 */
export function NotificationSettings() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const preferences = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => data.notifications.preferences(),
  });

  const save = useMutation({
    mutationFn: (change: NotificationPreference) =>
      data.notifications.updatePreferences([change]),
    onSuccess: (result) => {
      if (result.ok) {
        setProblem(null);
        queryClient.setQueryData(PREFERENCES_KEY, result.data);
      } else {
        setProblem(describe(result.rejection));
      }
    },
    onError: (error) => {
      setProblem(error.message);
    },
  });

  function enabled(kind: NotificationKind, channel: NotificationChannel) {
    return (
      preferences.data?.find(
        (row) => row.kind === kind && row.channel === channel,
      )?.enabled ?? true
    );
  }

  return (
    <PageContainer title="Notifications" breadcrumb="Account settings" padded>
      <div className="mx-auto max-w-3xl space-y-4">
        <p className="text-muted-foreground text-sm">
          Everything that needs you appears in the bell at the top of every
          page, and that is always on. Choose here what should also reach you by
          email or on your phone.
        </p>
        <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-sm">
          Email goes to the address on your account. Being assigned something or
          mentioned is emailed straight away; everything else you tick for email
          arrives together in one digest each morning, and only if you have not
          already read it in the app. Phone push works as soon as you turn it on
          for a device, below.
        </p>

        <PushDeviceCard />

        {preferences.isPending ? (
          <div className="space-y-3" aria-hidden>
            {NOTIFICATION_KINDS.map((entry) => (
              <Skeleton key={entry.kind} className="h-24 w-full" />
            ))}
          </div>
        ) : preferences.isError ? (
          <LoadFailed
            message={preferences.error.message}
            onRetry={() => {
              void preferences.refetch();
            }}
          />
        ) : (
          <ul className="space-y-3">
            {NOTIFICATION_KINDS.map((entry) => (
              <li key={entry.kind}>
                <Card>
                  <CardContent>
                    <fieldset className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <legend className="sr-only">{entry.label}</legend>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{entry.label}</p>
                        <p className="text-muted-foreground text-sm">
                          {entry.description}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-x-5 gap-y-2">
                        {NOTIFICATION_CHANNELS.map(({ channel, label }) => {
                          const id = `pref-${entry.kind}-${channel}`;
                          const always = channel === "in_app";
                          return (
                            <div
                              key={channel}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={id}
                                checked={enabled(entry.kind, channel)}
                                disabled={always}
                                onCheckedChange={(checked) => {
                                  save.mutate({
                                    kind: entry.kind,
                                    channel,
                                    enabled: checked === true,
                                  });
                                }}
                              />
                              <Label htmlFor={id} className="font-normal">
                                {label}
                                {always ? (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    (always)
                                  </span>
                                ) : channel === "email" ? (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    (
                                    {emailDelivery(entry.kind) === "at_once"
                                      ? "straight away"
                                      : "daily digest"}
                                    )
                                  </span>
                                ) : null}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </fieldset>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}

        {problem ? (
          <p role="alert" className="text-destructive text-sm">
            {problem}
          </p>
        ) : null}
      </div>
    </PageContainer>
  );
}
